import type { BuiltInTool } from './types'

const ASK_CEO = 'os.ask_ceo'

/** What the model reads when an `os.ask_ceo` call is refused because the CEO could not answer it question by question. */
export const CEO_QUESTIONS_HINT = `Call ${ASK_CEO} again with questions: [{ number, question, context, ctoRecommendation }], numbered from 1, one decision per question. Every question needs a ctoRecommendation: the answer you recommend and a one-line reason. If you cannot recommend one, say what you would need to know and give your default anyway.`

/** Asking the CEO: always a gate, and the run ends on the answer. */
export const OS_CEO_TOOLS: readonly BuiltInTool[] = [
    {
        name: ASK_CEO,
        family: 'os.ceo',
        summary: 'Ask the CEO questions, each with a recommended answer, and end the run on their decision.',
        description:
            'Ask the CEO questions and stop. This run waits for their decision, then ends. Pass questions: one decision each, numbered from 1, each answerable on its own, each with context (why it blocks, the options you see) and ctoRecommendation (the answer you recommend and why; required). Give the card a title (what the CEO is deciding, e.g. "Scope the CSV export") and a why (one line on why only they can answer) when you have them. The CEO answers each one: accepts your recommendation, answers it themselves, or asks you to explain further. Submit → outcome ceo_approved, Reject → ceo_rejected; the next task gets their answers as ceoAnswers and any note as ceoNote. Call it last.',
        inputSchema: {
            type: 'object',
            properties: {
                questions: {
                    type: 'array',
                    minItems: 1,
                    items: {
                        type: 'object',
                        properties: {
                            number: { type: 'integer', minimum: 1 },
                            question: { type: 'string', description: 'One decision, answerable on its own' },
                            context: { type: 'string', description: 'Why it blocks, and the options you see' },
                            ctoRecommendation: {
                                type: 'string',
                                description: 'The answer you recommend, and a one-line reason. Never empty.',
                            },
                        },
                        required: ['number', 'question', 'ctoRecommendation'],
                        additionalProperties: false,
                    },
                },
                context: { type: 'string', description: 'One line on what is blocked, and the issue URL' },
                title: {
                    type: 'string',
                    description:
                        'Optional. The card’s title: what the CEO is deciding, in plain words, e.g. "Scope the CSV export". One line; cut at 120 characters. Without it the card is titled with the task.',
                },
                why: {
                    type: 'string',
                    description:
                        'Optional. One line under the title: why this waits for the CEO, e.g. "Changes what customers are promised". Cut at 160 characters.',
                },
            },
            required: ['questions'],
            additionalProperties: false,
        },
        returns:
            "The CEO's decision, as the run's outcome: `ceo_approved` with `ceoAnswers` (one per question) and `ceoNote`, or `ceo_rejected` with `ceoNote`. The next task receives them.",
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The gate is found again by run and tool call, so a repeat never asks twice.',
        needs: { gate: 'always' },
        gateCard: { title: '{title}', why: '{why}' },
        errors: [
            {
                code: 'ceo_questions_invalid',
                when: 'No `questions`, or a question with a repeated or invalid number, no text or no `ctoRecommendation`. Recorded as `ceo_questions_invalid:<why>`, before any gate opens.',
                handling: 'hand_back',
                hint: CEO_QUESTIONS_HINT,
            },
        ],
        api: { method: 'POST', path: '/v1/runs/self/ask-ceo', scope: 'run:self' },
        examples: [
            {
                input: {
                    questions: [
                        {
                            number: 1,
                            question: 'Should the export include archived reports?',
                            context: 'Customers asked for both; including them doubles the file size.',
                            ctoRecommendation:
                                'No: archived reports are rarely opened, and a filter can add them later.',
                        },
                    ],
                    context: 'Scoping https://git.example.com/acme/app/issues/12',
                    title: 'Scope the CSV export',
                    why: 'Changes what customers are promised',
                },
            },
        ],
        public: true,
    },
]
